/**
 * Microphone, speaker and ringer selection persisted in localStorage; `devicechange`
 * re-lists and the line manager `replaceTrack()`s when the chosen mic disappears.
 */
export interface DevicePrefs {
  mic: string | null;
  speaker: string | null;
  ringer: string | null;
  /** ring on the speaker as well as the headset */
  ringAlsoSpeaker: boolean;
}

export const DEVICE_PREF_KEY = 'ccphone.devices';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export function loadDevicePrefs(storage: StorageLike | null = safeLocalStorage()): DevicePrefs {
  const d: DevicePrefs = { mic: null, speaker: null, ringer: null, ringAlsoSpeaker: false };
  try {
    const raw = storage?.getItem(DEVICE_PREF_KEY);
    if (!raw) return d;
    const j = JSON.parse(raw) as Partial<Record<keyof DevicePrefs, unknown>>;
    return {
      mic: typeof j.mic === 'string' ? j.mic : null,
      speaker: typeof j.speaker === 'string' ? j.speaker : null,
      ringer: typeof j.ringer === 'string' ? j.ringer : null,
      ringAlsoSpeaker: j.ringAlsoSpeaker === true,
    };
  } catch {
    return d;
  }
}

export function saveDevicePrefs(prefs: DevicePrefs, storage: StorageLike | null = safeLocalStorage()): void {
  try {
    storage?.setItem(DEVICE_PREF_KEY, JSON.stringify(prefs));
  } catch {
    /* quota / private mode: prefs live for the session only */
  }
}

export function safeLocalStorage(): StorageLike | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

export interface DeviceLists {
  mics: MediaDeviceInfo[];
  outputs: MediaDeviceInfo[];
}

export async function listDevices(md: Pick<MediaDevices, 'enumerateDevices'> | undefined = navigator.mediaDevices): Promise<DeviceLists> {
  if (!md) return { mics: [], outputs: [] };
  const all = await md.enumerateDevices();
  return { mics: all.filter((d) => d.kind === 'audioinput'), outputs: all.filter((d) => d.kind === 'audiooutput') };
}

/** the preferred id when it is still present, else null (= browser default) */
export function resolveDevice(preferred: string | null, present: readonly { deviceId: string }[]): string | null {
  if (!preferred) return null;
  return present.some((d) => d.deviceId === preferred) ? preferred : null;
}

export function micConstraints(prefs: Pick<DevicePrefs, 'mic'>): MediaStreamConstraints {
  const audio: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
  if (prefs.mic) audio.deviceId = { ideal: prefs.mic };
  return { audio, video: false };
}

export function watchDeviceChange(md: Pick<MediaDevices, 'addEventListener' | 'removeEventListener'> | undefined, cb: () => void): () => void {
  if (!md) return () => undefined;
  md.addEventListener('devicechange', cb);
  return () => md.removeEventListener('devicechange', cb);
}

/** setSinkId when the browser has it; a missing API or a failure is reported, never thrown */
export async function applySink(el: HTMLMediaElement, sinkId: string | null): Promise<boolean> {
  const fn = (el as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId;
  if (typeof fn !== 'function') return false;
  try {
    await fn.call(el, sinkId ?? '');
    return true;
  } catch {
    return false;
  }
}
